import { FC } from 'react';
import { round } from 'lodash';
import { differenceInDays, startOfDay } from 'date-fns';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { Row, Column } from 'further-ui/components/Layout';
import { CurrencyCode } from 'further-types/lp/vehicle';
import {
  CalendarDay,
  numberToCurrencyString,
  pluralize,
} from 'further-ui/utils';
import CapitalProgress from './CapitalProgress';

const CapitalProgressContainer = styled(Column, {
  shouldForwardProp: (prop) => prop !== 'width',
})<{ width: number | null }>(({ theme, width }) => ({
  padding: theme.spacing(theme.spacingSizes.sm, theme.spacingSizes.xl),
  width: width ? `${width - 2}px` : '100%',
  position: 'sticky',
  top: 0,
  left: 0,
}));

type Props = {
  capitalAction: {
    actionDueDate: CalendarDay;
    investorsPaid: number;
    totalInvestors: number;
    amountPaid: number;
    totalAmount: number;
  };
  tableWidth: number | null;
  baseCurrency?: CurrencyCode;
};

const CapitalActionCapitalProgress: FC<Props> = ({
  capitalAction,
  tableWidth,
  baseCurrency,
}) => {
  const {
    actionDueDate,
    investorsPaid,
    totalInvestors,
    amountPaid,
    totalAmount,
  } = capitalAction;
  const dueDate = actionDueDate.toUTCDate();
  const today = startOfDay(new Date());
  const daysUntilDue = differenceInDays(dueDate, today);
  const daysUntilDueColor = daysUntilDue <= 7 ? 'error' : 'grey.900';
  const daysUntilDueText =
    daysUntilDue > 0
      ? `Due in ${daysUntilDue} ${pluralize('day', daysUntilDue)}`
      : daysUntilDue === 0
      ? 'Due today'
      : 'Overdue';
  const progress =
    totalAmount > 0 ? round((amountPaid / totalAmount) * 100, 2) : 0;

  return (
    <CapitalProgressContainer spacing="sm" width={tableWidth}>
      <Typography variant="h6">{`${numberToCurrencyString(amountPaid, {
        currency: baseCurrency,
      })} of ${numberToCurrencyString(totalAmount, {
        currency: baseCurrency,
      })}`}</Typography>
      <CapitalProgress value={progress} />
      <Row spacing="sm" justify="space-between">
        <Typography variant="h6" color="grey.600">
          {`${investorsPaid}/${totalInvestors} investors paid`}
        </Typography>
        <Typography variant="h6" color={daysUntilDueColor}>
          {daysUntilDueText}
        </Typography>
      </Row>
    </CapitalProgressContainer>
  );
};

export default CapitalActionCapitalProgress;
